<template>
	<div class="workflow-header">
		<div class="wf-row1">
			<div class="workflow-headline">
				<input
					class="headline H5-5 mobile-headline-6"
					type="text"
					ref="editTitle"
					v-if="editWorkflowTitle"
					:placeholder="workflowTitle"
					@blur="onTitleEdit"
					@keydown.enter="endTitleEdit"
					v-model="setUpdatedTitle"
				/>
				<h5 class="H5-5 mobile-headline-6" v-else>{{ workflowTitle }}</h5>
				<md-button class="md-icon-button md-dense edit-icon mobile-hide" @click="editTitle">
					<base-icon icon-name="pen" />
				</md-button>
			</div>
			<div class="workflow-buttons" data-tour="wf-step3" id="tour-step3">
				<md-button class="md-primary md-button-large" @click="tourActive ? '#' : $emit('showMyWorkflows')">
					<base-icon icon-name="list" iconSize="18px" />
					my experiments
				</md-button>

				<div :name="$el" class="reset-button-wrapper">
					<tippy
						v-if="isSavedWorkflow || isSharedWorkflow"
						:to="$el"
						placement="top-end"
						theme="product-relations-tooltip"
						distance="2"
						offset="80, 0"
					>
						Functionality unavailable for saved and shared experiments
					</tippy>

					<md-button
						class="md-primary md-button-large mobile-hide"
						@click="tourActive ? '#' : handleResetClick()"
						:disabled="isSavedWorkflow || isSharedWorkflow"
					>
						<base-icon icon-name="reset" iconSize="18px" />
						reset
					</md-button>
				</div>

				<md-button
					class="md-primary md-raised md-button-large"
					ref="saveBtn"
					:style="saveBtnAttr.style"
					:disabled="!saveButtonEnabled"
					@click="tourActive ? '#' : saveWorkflow()"
				>
					<spinner v-if="saveBtnAttr.state === 'saving'" key="saving" class="save-btn-icon" />
					<base-icon
						v-if="saveBtnAttr.state === 'saved'"
						icon-name="check"
						key="saved"
						class="save-btn-icon"
						id="saved-check"
					/>
					{{ saveBtnAttr.text }}
				</md-button>
			</div>
		</div>
		<div class="wf-row2">
			<div class="workflow-links">
				<span data-tour="wf-step4" id="tour-step4" style="display: flex">
					<div style="flex: 0" class="mobile-hide">
						<dropdown-custom class="application">
							<template v-slot:opener="buttonScopeDownload">
								<md-button
									@click="tourActive ? '#' : buttonScopeDownload.handleClick()"
									class="md-primary"
								>
									<base-icon icon-name="download" />
									Download
								</md-button>
							</template>
							<template v-slot:list="listScopeDownload">
								<base-dropdown-list
									:open="listScopeDownload.open"
									:options="dropdownItemsDownload"
									:listWidth="220"
									:overflow-y-auto="true"
								/>
							</template>
						</dropdown-custom>
					</div>
					<md-button class="md-primary" @click="tourActive ? '#' : handleShareClick()">
						<base-icon icon-name="share" />
						Share
					</md-button>
				</span>
				<div style="flex: 0">
					<md-button class="md-primary" @click="startManualTour()">
						<base-icon icon-name="help" />
						Take a tour
					</md-button>
				</div>
			</div>
			<div class="workflow-view-mode">
				<div class="tour-wrapper" data-tour="wf-step5" id="tour-step5">
					<div class="eln-switch-group" data-tour="wf-step5" id="tour-step5">
						<div class="btn-group" role="group">
							<label
								type="button"
								name="button"
								class="btn"
								:class="{active: !isLabNotesActive}"
								@click="tourActive ? '#' : switchToWorkflowMode()"
							>
								<span>Experiment</span>
							</label>
							<label
								data-cy="lab-notes-button"
								type="button"
								name="button"
								class="btn"
								:class="{active: isLabNotesActive}"
								@click="tourActive ? '#' : switchToLabNotesMode()"
							>
								<base-icon v-if="isLabNotesLockActive" icon-name="lock" icon-size="21px" />
								<span>Lab Notes & Protocols</span>
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>
		<workflow-tags :labNotesActive="isLabNotesActive"></workflow-tags>
	</div>
</template>
<script>
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';
import BaseDropdownList from '@/components/Dropdown/BaseDropdown/BaseDropdownList.vue';
import WorkflowTags from './WorkflowTags.vue';
import {trackEvent} from '@/services/tracking.js';
import Spinner from './Spinner.vue';
import tour from '@/mixins/tour';

export default {
	name: 'WorkflowHeader',
	components: {
		Spinner,
		DropdownCustom,
		BaseDropdownList,
		WorkflowTags,
	},
	mixins: [tour],
	props: {
		saveButtonEnabled: {
			default: false,
			type: Boolean,
		},
		saveBtnAttr: {
			required: true,
		},
		enableShareUrl: {
			type: Boolean,
		},
		labNotesActive: {
			type: Boolean,
			default: false,
		},
		labNotesLockActive: {
			type: Boolean,
			default: true,
		},
		tourActive: {
			type: Boolean,
			default: false,
		},
		isSavedWorkflow: {
			type: Boolean,
			default: false,
		},
		isSharedWorkflow: {
			type: Boolean,
			default: false,
		},
		isMobile: {
			type: Boolean,
			default: false,
		},
	},
	created() {
		this.$bus.$on('updateSharedTitle', this.updateWorkflowTitle);
	},
	mounted() {
		this.updateWorkflowTitle();
		this.$root.$on('saveWorkflowSuccess', this.updateWorkflowTitle);
		this.$root.$on('updateMyWfHeader', this.updateWorkflowTitle);
	},
	computed: {
		workflowDefaultHeadline() {
			return this.$store.state.workflowDefaultHeadline;
		},
		workflowHeadline() {
			return this.$store.state.workflowHeadline;
		},
		workflowTitle() {
			return !this.updatedTitle ? this.workflowDefaultHeadline : this.updatedTitle;
		},
		setUpdatedTitle: {
			get() {
				return this.updatedTitle;
			},
			set(newVal) {
				this.updatedTitle = newVal;
				this.$store.commit('setWorkflowHeadline', newVal);
				this.$root.$emit('changeRegistered');
			},
		},
		dropdownItemsDownload() {
			return [
				{
					key: 0,
					value: {
						enUS: 'Download PDF',
					},
					iconColor: '#8c8c8c',
					iconName: 'download',
					callback: this.downloadPDF,
				},
				{
					key: 1,
					value: {
						enUS: 'Download CSV',
					},
					iconName: 'download',
					iconColor: '#8c8c8c',
					disabledIconColor: '#999',
					disabledIconName: 'lock',
					callback: this.downloadCSV,
					disabled: this.isDownloadCSVButtonDisabled,
					isTooltipShown: this.isDownloadCSVButtonDisabled,
					toolTipMessage: 'Log in to discover the full range of functionality',
				},
			];
		},
		isDownloadCSVButtonDisabled() {
			return !this.$store.state.jwt;
		},
		isLabNotesActive() {
			return this.labNotesActive;
		},
		isLabNotesLockActive() {
			return this.labNotesLockActive;
		},
	},
	data() {
		return {
			editWorkflowTitle: false,
			updatedTitle: null,
		};
	},
	methods: {
		handleResetClick() {
			if (this.isSavedWorkflow) {
				return;
			}
			return this.$emit('resetWorkflow');
		},
		handleShareClick() {
			this.$emit('shareWorkflow');
			return this.trackShare();
		},
		async updateWorkflowTitle() {
			this.updatedTitle = await this.workflowHeadline;
		},
		editTitle() {
			this.editWorkflowTitle = !this.editWorkflowTitle;
			this.$nextTick(() => {
				this.$refs.editTitle.focus();
			});
		},
		async onTitleEdit() {
			this.editWorkflowTitle = !this.editWorkflowTitle;
		},
		endTitleEdit() {
			this.$refs.editTitle.blur();
		},
		saveWorkflow() {
			this.$emit('saveWorkflow');
		},
		downloadCSV() {
			this.$emit('downloadCSV');
		},
		downloadPDF() {
			this.$emit('downloadPDF');
		},
		startManualTour() {
			this.resetTourStorage();
		},
		trackShare() {
			trackEvent('ctaClick', {eVar58: 'Share workflow| Workflow Configurator'});
		},
		switchToWorkflowMode() {
			this.$emit('setLabNotesActive', false);
			trackEvent('ctaClick', {eVar58: 'Workflow Mode| Workflow Configurator'});
		},
		switchToLabNotesMode() {
			this.$emit('setLabNotesActive', true);
			trackEvent('ctaClick', {eVar58: 'Lab Notebook Mode| Workflow Configurator'});
		},
	},
};
</script>

<style lang="scss">
/* save button animations */
#saved-check {
	display: inline;
	vertical-align: middle;
	position: relative;
	top: 4px;
}
.save-btn-icon {
	opacity: 1;
	width: 24px;
}
.saving-fade-enter,
.saving-fade-leave-to {
	opacity: 0;
	width: 12px;
	transform: translateX(10px);
}
.saving-fade-enter-active {
	transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}
.saving-fade-leave-active {
	opacity: 0;
	width: 0px;
	transition: all 0s ease;
}
.saved-fade-enter {
	opacity: 0;
	width: 24px;
}
.saved-fade-enter-active {
	transition: all 0.6s ease;
}
.saved-fade-leave-active {
	opacity: 0;
	width: 0px;
	transition: all 0.5s ease;
}

.workflow-header {
	@include box(100%, auto);
	margin-top: 30px;
	margin-bottom: 8px;
	@media (max-width: $mobile) {
		margin-top: 20px;
		h5 {
			margin: 0;
		}
	}
	h5,
	input.headline {
		overflow-wrap: break-word;
		word-wrap: break-word;
		word-break: break-word;
	}
	input.headline {
		flex: 1;
		padding: 0 6px;
		box-shadow: none;
		&:focus {
			outline: none;
		}
		@media (max-width: $mobile) {
			overflow: hidden;
			word-break: normal;
			word-wrap: normal;
			border: 1px solid $color-grey;
			border-radius: 0;
			-webkit-appearance: none;
			-moz-appearance: none;
			appearance: none;
		}
	}
	.edit-icon {
		color: $color-mid-grey;
		@media (min-width: $mobile + 1) {
			margin-top: 10px;
		}
		&:hover {
			&:before {
				opacity: 0;
			}
			color: $color-blue;
		}
	}
	.wf-row1 {
		@include flexRow;
		margin-bottom: 10px;
		justify-content: space-between;
		> div {
			@include flexRow;
			flex: 2;
		}
		.workflow-buttons {
			align-self: center;
			flex: 1;
			justify-content: space-between;

			@media (max-width: $mobile) {
				position: fixed;
				bottom: 0;
				background: white;
				width: 100%;
				left: 0;
				z-index: 6;
				padding: 12px 16px;
				box-shadow: 0px -2px 4px 0px rgb(8 35 48 / 24%);
				justify-content: flex-end;

				button:nth-child(1) {
					margin-right: 16px;
				}
			}

			.md-button {
				.base-icons {
					position: relative;
					bottom: 2px;
					margin-right: 5px;
				}
			}
		}
	}
	.wf-row2 {
		@include flexRow;

		@media (max-width: $tablet-sm+64) {
			@include flexColumn;
		}
		margin-bottom: 2px;
		> div {
			flex: 1;
			@include flexRow;
		}
		.icons-flag {
			position: relative;
			left: 6px;
			margin-right: 28px;
		}
		div:nth-child(2) {
			justify-content: flex-end;
		}
		.workflow-view-mode {
			.tour-wrapper {
				display: flex;
				align-items: center;
			}
			@media (min-width: $mobile+1) and (max-width: $tablet) {
				label:nth-child(1) {
					background-color: $color-blue;
					color: white;
				}
			}
			@include flexRow;
			@media (max-width: $tablet-sm+64) {
				margin-top: 20px;
			}
			align-items: center;
			span.caption-text {
				@include font-styles('Futura W01 Book', 400);
				@include textStyles(14, 17.95, 0.5);
				color: $color-grey;
				margin-right: 16px;
				@media (max-width: 677px) {
					margin-right: 8px;
				}
			}
			.eln-switch-group {
				background-color: transparent;
				border: 1px solid $color-blue;
				border-radius: 8px;
				height: 32px;
				.btn-group {
					color: $color-blue;
					display: flex;
					align-items: center;
					justify-content: center;
					margin: 0;
					top: -1px;
					label {
						@include font-styles('Futura W01 Medium', 500);
						@include textStyles(16, 24, 0.4);
						height: 32px;
						background: transparent;
						color: $color-blue;
						border-radius: 8px;
						padding-bottom: 5px;
						padding-top: 5px;
						left: -1px;
						span {
							position: relative;
							top: -2px;
						}
					}
					label.active {
						box-shadow: none;
						background-color: $color-blue;
						color: $white;
					}
					label:focus,
					label:active {
						box-shadow: none;
						outline: none;
					}
					label:nth-child(1) {
						@media (min-width: $mobile+1) and (max-width: $mobile+30) {
							width: 90px;
						}
						width: 100px;
					}
					label:nth-child(2) {
						display: flex;
						left: 1px;
						outline: none !important;
						span {
							width: 100%;
						}
						i {
							@media (min-width: $mobile+1) and (max-width: $mobile+30) {
								left: -8px;
								margin-right: -4px;
							}
							@media (min-width: $mobile+1) and (max-width: $mobile+20) {
								left: -10px;
								margin-right: -10px;
							}
							position: relative;
							top: -2px;
							margin-right: 8px;
						}
					}
				}
			}
		}
		.workflow-links button {
			text-transform: capitalize;
		}
		@media (max-width: $mobile) {
			margin-top: 28px;
			.workflow-view-mode {
				display: none;
			}
		}
	}
	&.fixed {
		position: fixed;
		top: 0;
		left: 0;
		background: white;
		z-index: 99;
		padding: 13px 32px;
		margin: 0;
		box-shadow: rgba(0, 0, 0, 0.4) 0px 0px 0.5rem 0.0625rem;
		.wf-row1,
		.wf-row2 {
			max-width: 1206px;
			margin: 0 auto;
		}
		.workflow-headline {
			flex: 3 !important;
			h5 {
				font-weight: 500;
				font-size: 18px;
				line-height: 28px;
				letter-spacing: 0.1px;
				margin: 0;
			}
			.edit-icon {
				padding: 0 10px;
				min-height: 0;
			}
		}
		.workflow-buttons {
			align-self: flex-start;
			button {
				padding: 8px 15.76px !important;
				min-height: 21px;
				min-width: 0;
			}
			.icon-btn {
				font-size: 16px;
				line-height: 21px;
				letter-spacing: 1.25px;
			}
		}
		.workflow-links {
			margin-left: -7px;
			button {
				padding: 1px 5px !important;
				min-height: 0;
				font-size: 14px;
				margin-right: 2%;
				&:last-of-type {
					margin-right: 0;
				}
				.icon {
					width: 20%;
					min-width: 0 !important;
				}
			}
		}
		.workflow-view-mode {
			display: none;
		}
	}
	.material-icons-outlined {
		vertical-align: middle;
	}

	.dropdown.application.open {
		.md-ripple {
			background-color: $color-light-blue;
		}
	}
}
</style>
